import React, { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { CONTACT_EMAIL } from '../../config'

function CopyEmailButton({ email }: { email: string }) {
  const [copied, setCopied] = React.useState(false)

  function handleCopy() {
    navigator.clipboard.writeText(email).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="font-bold underline underline-offset-2 transition-colors cursor-pointer"
      style={{ color: '#111', background: 'none', border: 'none', padding: 0, fontSize: 'inherit' }}
    >
      {copied ? 'Copied!' : email}
    </button>
  )
}

// Hand-drawn squiggle, matches landing page hero accent
function Squiggle() {
  return (
    <svg
      aria-hidden
      viewBox="0 0 200 12"
      width="180"
      height="12"
      fill="none"
      style={{ display: 'block', marginTop: 4 }}
    >
      <path
        d="M2 7 C 25 -1, 50 11, 75 5 S 125 -1, 150 6 S 190 11, 198 3"
        stroke="var(--accent)"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  )
}

export function AuthShell({
  title,
  subtitle,
  children,
  compact = false,
  narrowLeft = false,
  leftTitle,
  leftSubtitle,
  leftTips,
}: {
  title: string
  subtitle?: string
  children: ReactNode
  compact?: boolean
  narrowLeft?: boolean
  leftTitle?: string
  leftSubtitle?: string
  leftTips?: string[]
}) {
  // Light-only brand identity (matches landing page)
  React.useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'light')
    window.localStorage.setItem('nudge-theme', 'light')
  }, [])

  return (
    <div
      className={`min-h-screen flex items-center justify-center px-6 ${compact ? 'py-6' : 'py-14'}`}
      style={{
        backgroundColor: '#FBE6D4',
        fontFamily: "'Manrope', sans-serif",
      }}
    >
      {/* Spot doodles — Mailchimp-style background accents */}
      <svg
        aria-hidden
        className="absolute top-16 right-16 hidden lg:block pointer-events-none"
        width="56"
        height="56"
        viewBox="0 0 56 56"
        fill="none"
      >
        <circle cx="28" cy="28" r="17" stroke="#111" strokeWidth="2.25" />
        <path d="M12 28 Q 28 7, 44 28 Q 28 49, 12 28" stroke="#111" strokeWidth="2.25" fill="none" />
      </svg>

      <div
        className="auth-card relative w-full max-w-5xl overflow-hidden"
        style={{
          backgroundColor: '#fff',
          border: '2px solid #111',
          borderRadius: 22,
          boxShadow: '8px 8px 0 #111',
        }}
      >
        <div className={`grid grid-cols-1 ${narrowLeft ? 'lg:grid-cols-[1fr_3fr]' : 'lg:grid-cols-2'}`}>

          {/* ── LEFT HERO PANEL ──────────────────────────────────────── */}
          <div
            className={`relative ${compact ? 'p-7 lg:p-9' : 'p-10 lg:p-12'} flex flex-col justify-between`}
            style={{
              backgroundColor: '#FBE6D4',
              borderRight: '2px solid #111',
            }}
          >
            {/* Brand lockup */}
            <div className="relative z-10">
              <a href="/" className="inline-flex items-center gap-2.5 no-underline">
                <img src="/logo.svg" alt="" className="h-8 w-8" aria-hidden />
                <span
                  className="font-extrabold text-2xl tracking-tight"
                  style={{ color: '#111', letterSpacing: '-0.02em' }}
                >
                  Nudge
                </span>
              </a>

              <h2
                className="mt-12 font-extrabold tracking-tight"
                style={{
                  color: '#111',
                  fontSize: 'clamp(1.75rem, 3.2vw, 2.5rem)',
                  lineHeight: 1.05,
                  letterSpacing: '-0.025em',
                }}
              >
                {leftTitle ? (
                  <>
                    {leftTitle}
                    <Squiggle />
                  </>
                ) : (
                  <>
                    Clarity for your{' '}
                    <span style={{ position: 'relative', display: 'inline-block', color: 'var(--accent)' }}>
                      business.
                    </span>
                    <Squiggle />
                  </>
                )}
              </h2>
              <p
                className="mt-5 text-base"
                style={{ color: '#3a2e26', lineHeight: 1.55, maxWidth: 380 }}
              >
                {leftSubtitle ?? 'Sign in to continue. From busy to in control.'}
              </p>

              {leftTips && leftTips.length > 0 ? (
                <ul
                  className="mt-7 space-y-2.5 text-sm font-medium"
                  style={{ color: '#3a2e26', maxWidth: 380 }}
                >
                  {leftTips.map(tip => (
                    <li key={tip} className="flex items-start gap-2">
                      <svg
                        className="w-4 h-4 flex-shrink-0 mt-0.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="#111"
                        strokeWidth={3}
                        aria-hidden
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                      {tip}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>

            {/* Stats row — flat black-bordered tiles */}
            {compact ? null : (
              <div
                className="relative z-10 mt-10 grid grid-cols-3 gap-3"
              >
                {[
                  { val: '60s', label: 'Setup' },
                  { val: '1', label: 'Inbox' },
                  { val: '0', label: 'Guessing' },
                ].map(({ val, label }) => (
                  <div
                    key={label}
                    className="flex flex-col items-start"
                    style={{
                      backgroundColor: '#fff',
                      border: '2px solid #111',
                      borderRadius: 12,
                      padding: '12px 14px',
                      boxShadow: '3px 3px 0 #111',
                    }}
                  >
                    <div
                      className="font-extrabold"
                      style={{
                        color: '#111',
                        fontSize: '1.5rem',
                        lineHeight: 1,
                        letterSpacing: '-0.02em',
                      }}
                    >
                      {val}
                    </div>
                    <div
                      className="mt-1.5 text-[10px] font-bold font-dm-mono uppercase"
                      style={{ letterSpacing: '0.12em', color: '#5a4736' }}
                    >
                      {label}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── RIGHT FORM PANEL ─────────────────────────────────────── */}
          <div
            className={`auth-form-panel ${compact ? 'p-7 lg:p-9' : 'p-10 lg:p-12'}`}
            style={{ backgroundColor: '#fff' }}
          >
            {/* Back to home */}
            <div className="mb-7">
              <Link
                to="/"
                className="auth-back-pill inline-flex items-center gap-2 px-3.5 py-1.5 text-xs font-bold transition-transform"
                style={{
                  border: '2px solid #111',
                  color: '#111',
                  backgroundColor: '#FBE6D4',
                  borderRadius: 999,
                  boxShadow: '2px 2px 0 #111',
                  textDecoration: 'none',
                }}
              >
                <span aria-hidden>←</span>
                Back to home
              </Link>
            </div>

            {/* Page title */}
            <div className="mb-7">
              <h1
                className="font-extrabold tracking-tight"
                style={{
                  color: '#111',
                  fontSize: '1.75rem',
                  lineHeight: 1.1,
                  letterSpacing: '-0.02em',
                }}
              >
                {title}
              </h1>
              {subtitle ? (
                <p className="mt-2 text-sm" style={{ color: '#3a2e26', lineHeight: 1.55 }}>
                  {subtitle}
                </p>
              ) : null}
            </div>

            {children}

            {/* Divider + help + terms */}
            {compact ? null : (
              <>
                <div
                  className="mt-7"
                  style={{ height: 2, backgroundColor: '#111', opacity: 0.08 }}
                  aria-hidden
                />

                <div
                  className="mt-5 flex items-center gap-2.5 px-3.5 py-2.5"
                  style={{
                    border: '2px solid #111',
                    backgroundColor: '#FBE6D4',
                    borderRadius: 12,
                    boxShadow: '3px 3px 0 #111',
                  }}
                >
                  <span
                    className="inline-flex h-7 w-7 items-center justify-center text-xs font-extrabold"
                    style={{
                      backgroundColor: '#111',
                      color: 'var(--accent)',
                      borderRadius: 999,
                    }}
                    aria-hidden
                  >
                    ?
                  </span>
                  <span className="text-xs font-medium" style={{ color: '#3a2e26' }}>
                    Need help? Email us at{' '}
                    <CopyEmailButton email={CONTACT_EMAIL} />
                  </span>
                </div>

                <p className="mt-5 text-xs leading-relaxed" style={{ color: '#5a4736' }}>
                  By continuing, you agree to our{' '}
                  <a
                    href="/terms"
                    className="font-bold underline underline-offset-2"
                    style={{ color: '#111' }}
                  >
                    Terms
                  </a>{' '}
                  and acknowledge our{' '}
                  <a
                    href="/privacy"
                    className="font-bold underline underline-offset-2"
                    style={{ color: '#111' }}
                  >
                    Privacy Policy
                  </a>
                  .
                </p>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Scoped overrides — restyle children inputs/buttons without touching their files */}
      <style>{`
        .auth-form-panel label {
          color: #111 !important;
          font-weight: 700;
        }
        .auth-form-panel .nudge-input {
          border: 1.5px solid #111 !important;
          background-color: #fff !important;
          border-radius: 12px !important;
          color: #111 !important;
          padding: 11px 14px !important;
          font-weight: 500;
          transition: box-shadow 150ms ease;
        }
        .auth-form-panel .nudge-input::placeholder { color: #8a7869; }
        .auth-form-panel .nudge-input:focus {
          outline: none !important;
          box-shadow: 3px 3px 0 #111 !important;
          background-color: #fff !important;
        }
        .auth-form-panel .nudge-input:disabled {
          background-color: #FAF5EC !important;
          opacity: 0.7;
        }

        /* Primary CTA — any submit button or button with the explicit accent bg */
        .auth-form-panel button[type="submit"] {
          background-color: var(--accent) !important;
          color: #fff !important;
          border: 2px solid #111 !important;
          border-radius: 999px !important;
          padding: 13px 20px !important;
          font-weight: 700 !important;
          box-shadow: 3px 3px 0 #111 !important;
          transition: transform 150ms ease, box-shadow 150ms ease;
        }
        .auth-form-panel button[type="submit"]:hover:not(:disabled) {
          transform: translate(-2px, -2px);
          box-shadow: 5px 5px 0 #111 !important;
        }
        .auth-form-panel button[type="submit"]:active:not(:disabled) {
          transform: translate(1px, 1px);
          box-shadow: 1px 1px 0 #111 !important;
        }
        .auth-form-panel button[type="submit"]:disabled {
          opacity: 0.55;
          cursor: not-allowed;
        }

        /* Inline anchor links inside the form (sign-up/sign-in cross-links, forgot password) */
        .auth-form-panel a {
          color: #111;
          font-weight: 700;
          text-decoration: underline;
          text-underline-offset: 3px;
        }
        .auth-form-panel a:hover { color: var(--accent); }

        /* Error message blocks (the rounded box around { error } text) */
        .auth-form-panel form > div[class*="rounded"] {
          border: 2px solid #111 !important;
          background-color: #FFD9D9 !important;
          color: #111 !important;
          border-radius: 12px !important;
        }

        /* Back-to-home + help interactions */
        .auth-back-pill:hover {
          transform: translate(-1px, -1px);
          box-shadow: 3px 3px 0 #111 !important;
        }
      `}</style>
    </div>
  )
}
